<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="isEdit ? update() : create()" ref="eventForm">
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :rules="[val => val !== '' || $t('Enter a name')]"
                :label="$t('Name')"
                class="q-mb-xs q-mt-lg"
                bg-color="white"
                clearable
                lazy-rules
                outlined
                color="accent"
            />
            <q-input
                v-model="form.code"
                @clear="form.code = ''"
                :label="$t('SKU')"
                :rules="[val => val !== '' || $t('Enter an SKU')]"
                class="q-mb-xs"
                bg-color="white"
                outlined
                clearable
                color="accent"
            />
            <q-input
                v-model="form.description"
                @clear="form.description = ''"
                :label="$t('Description')"
                class="q-mb-lg"
                bg-color="white"
                clearable
                outlined
                color="accent"
            />
            <q-select
                v-model="form.unit_measure"
                @clear="form.unit_measure = ''"
                :options="unitMeasureOptions"
                :label="$t('Unit measure')"
                :rules="[val => val !== '' || $t('Select a unit measure')]"
                class="q-mb-xs"
                bg-color="white"
                color="accent"
                emit-value
                map-options
                lazy-rules
                outlined
            />
            <q-input
                v-model="form.unit_packing"
                @clear="form.unit_packing = ''"
                :label="$t('Unit packing')"
                :rules="[val => validateUnitPacking(val)]"
                class="q-mb-xs"
                bg-color="white"
                fill-mask="0"
                reverse-fill-mask
                clearable
                lazy-rules
                outlined
                color="accent"
            />
            <product-property
                v-if="showProductCategory"
                :category="form.product_category"
                :properties="form.properties"
                @update:product-category="updateProductCategory"
                @update:product-property="updateProductProperty"
            />
            <div class="form__actions">
                <div>
                    <q-btn
                        v-if="isEdit"
                        @click="handleRemove"
                        :color="
                            $userCannot('edit', 'products')
                                ? 'grey'
                                : 'negative'
                        "
                        :disable="$userCannot('edit', 'products')"
                        class="float-left q-px-xs bg-white"
                        icon="fas fa-trash-alt"
                        outline
                    ></q-btn>
                    <q-tooltip v-if="$userCannot('edit', 'products')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <div>
                    <q-btn
                        :label="$t('Save')"
                        :color="
                            $userCannot('edit', 'products') ? 'grey' : 'primary'
                        "
                        :disable="$userCannot('edit', 'products')"
                        type="submit"
                        class="float-right"
                    />
                    <q-tooltip v-if="$userCannot('edit', 'products')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <q-btn
                    @click="$router.go(-1)"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm bg-white"
                    text-color="primary"
                    color="white"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import ProductProperty from '@components/ProductProperty.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { convertToUnitSystem, getUnitSystemOptions } from '@models/products.js';
import { roundTo } from '@utils/numberFormatters.js';
import { mapState, mapActions } from 'pinia';
import { useProductsStore } from '@stores/products.js';
import { useFacilitiesStore } from '@stores/facilities.ts';

export default {
    name: 'ProductDetail',
    mixins: [BreadcrumbsManager],
    components: { ProductProperty },
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        },
        productId: {
            type: [Number, String],
            required: false,
            default: null
        }
    },
    mounted() {
        // init form
        if (this.isEdit) {
            this.initForm();
        }

        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Products'),
                route: {
                    name: 'product_list',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.isEdit
                    ? history.state.name
                    : this.$t('Create product')
            }
        ]);
    },
    data() {
        return {
            baseURL: 'products/',
            form: {
                facility: this.facilityId,
                name: '',
                code: '',
                description: '',
                product_category: null,
                properties: null,
                unit_measure: '',
                unit_packing: null
            },
            selectedProperties: []
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useProductsStore, [
            'getProductCategories',
            'getProductCategory'
        ]),
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get unit system of the facility.
         * @return {String} - Unit system.
         */
        unitSystem() {
            return this.facility?.unit_system;
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return this.productId !== null;
        },
        /**
         * Payload in server format.
         */
        payload() {
            // copy all fields
            const payload = Object.assign({}, this.form);

            // convert all units to use the metric system as the backend expects metric units
            const { value: metric_unit_packing, unit: metric_unit_measure } =
                convertToUnitSystem(
                    parseFloat(this.form['unit_packing']),
                    this.form['unit_measure'],
                    'metric' // ensure to convert to metric
                );

            payload['properties'] = this.selectedProperties;
            // API endpoint requires 12 digits max and 3 decimal places
            payload['unit_packing'] = roundTo(metric_unit_packing, 3);
            payload['unit_measure'] = metric_unit_measure;

            return payload;
        },
        /**
         * Get unit measure options according to facility unit system.
         * @return {Array} - Unit measure options.
         */
        unitMeasureOptions() {
            return getUnitSystemOptions(this.unitSystem);
        },
        /**
         * Determines if there are any product categories to show
         * to show/hide the product category select.
         * @returns {boolean} - Returns true if there are one or more product categories, otherwise false.
         */
        showProductCategory() {
            return this.getProductCategories.length > 0;
        }
    },
    methods: {
        convertToUnitSystem,
        ...mapActions(useProductsStore, ['formatAndSetProductAction']),
        /**
         * Initialize unit packing and measure according to facility unit system.
         */
        initUnitMeasureAndPacking() {
            const {
                value: converted_unit_packing,
                unit: converted_unit_measure
            } = convertToUnitSystem(
                parseFloat(history.state.unitPacking),
                history.state.unitMeasure,
                this.unitSystem
            );

            this.form['unit_packing'] = roundTo(converted_unit_packing, 3);
            this.form['unit_measure'] = converted_unit_measure;
        },
        /**
         * Initialize form.
         */
        initForm() {
            this.form['name'] = history.state.name;
            this.form['code'] = history.state.code;
            this.form['description'] = history.state.description;

            const hasValidProductCategory =
                this.getProductCategory(history.state.productCategory) !== null;

            // If the product category was deleted, do not set it
            if (hasValidProductCategory) {
                this.form['product_category'] = history.state.productCategory;
                this.form['properties'] = history.state.productProperties;
                // Preserve selected properties when editing a product.
                // This ensures that unchanged properties are retained in the payload,
                // since the computed property for the payload depends on these selections.
                // Without this, unmodified properties may be omitted during an update.
                this.selectedProperties = JSON.parse(
                    history.state.productProperties
                );
            }

            this.initUnitMeasureAndPacking();
        },
        /**
         * Create product.
         */
        async create() {
            try {
                let response = await this.$axios.post(
                    this.baseURL,
                    this.payload
                );
                this.formatAndSetProductAction(response.data);
                this.$notifySuccess(this.$t('Product created'));
                this.$router.go(-1);
            } catch (error) {
                this.handleApiErrors(error, 'create');
            }
        },
        /**
         * Handles API errors by performing the specified action.
         *
         * @param {Object} error - The error object returned from the API.
         * @param {String} action - The action that was being performed when the error occurred.
         */
        handleApiErrors(e, action) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            const apiErrors = {
                unique: 'product_code_already_exists',
                maxLength: 'Ensure this field has no more than 100 characters.',
                descriptionMaxLength:
                    'Ensure this field has no more than 150 characters.'
            };
            // Handle non unique product code error
            if (
                error.error_code &&
                error.error_code[0].includes(apiErrors.unique)
            ) {
                this.$notifyError(
                    this.$t(
                        'A product with the same SKU already exists in this facility.'
                    )
                );
            } else if (
                error.unit_packing &&
                error.unit_packing[0].includes('This field may not be null.')
            ) {
                this.$notifyError(
                    this.$t('Ensure the unit packing is a numerical value.')
                );
            } else if (
                error.name &&
                error.name[0].includes(apiErrors.maxLength)
            ) {
                this.$notifyError(
                    this.$t('Ensure the name has no more than 100 characters.')
                );

                // Handle code length error
            } else if (
                error.code &&
                error.code[0].includes(apiErrors.maxLength)
            ) {
                this.$notifyError(
                    this.$t('Ensure the SKU has no more than 100 characters.')
                );

                // Handle description length error
            } else if (
                error.description &&
                error.description[0].includes(apiErrors.descriptionMaxLength)
            ) {
                this.$notifyError(
                    this.$t(
                        'Ensure the description has no more than 150 characters.'
                    )
                );
                // Handle other errors
            } else if (error.product_category) {
                this.$notifyError(
                    this.$t('Ensure the product category is valid.')
                );
            } else {
                if (action === 'update') {
                    this.$notifyError(
                        this.$t(
                            'Failed to update product. Please try again later.'
                        ),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to create product. Please try again later.'
                        ),
                        error
                    );
                }
            }
        },
        /**
         * Update product.
         */
        async update() {
            await this.$axios
                .put(`${this.baseURL}${this.productId}/`, this.payload)
                .then(response => {
                    this.formatAndSetProductAction(response.data);
                    this.$notifySuccess(this.$t('Product updated'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.handleApiErrors(error, 'update');
                });
        },
        /**
         * Handle remove product.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this product?')
            ).onOk(async () => {
                await this.remove();
            });
        },
        /**
         * Remove product.
         */
        async remove() {
            await this.$axios
                .delete(`${this.baseURL}${this.productId}/`)
                .then(response => {
                    this.$notifySuccess(this.$t('Product deleted'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to remove product. Please try again later.'
                        ),
                        error
                    );
                });
        },
        /**
         * Update selected product category in the form payload
         * @param {object} category
         */
        updateProductCategory(selectedCategory) {
            this.form['product_category'] = selectedCategory?.value ?? null;
        },
        /**
         * Update selected product properties in the form payload
         * @param {object} selectedProperties
         */
        updateProductProperty(selectedProperties) {
            let updatedAssignedProperties = [];
            for (let property of selectedProperties) {
                updatedAssignedProperties.push({
                    product_property: property.product_property.value,
                    product_property_allowed_value:
                        property.product_property_allowed_value.value
                });
            }
            this.selectedProperties = [...updatedAssignedProperties];
        },
        /**
         * Validate unit packing.
         * @param {Number} value - Unit packing value.
         * @return {Boolean/String} - true if valid, error message otherwise.
         */
        validateUnitPacking(value) {
            if (!value) {
                // Handles null and undefined cases
                return this.$t('Enter a unit packing');
            }

            if (value <= 0) {
                return this.$t('Unit packing should be greater than 0');
            }

            const decimalPart = value.toString().split('.')[1];
            if (decimalPart && decimalPart.length > 3) {
                return this.$t(
                    'Unit packing should have at most 3 decimal places'
                );
            }

            return true;
        }
    }
};
</script>

<style lang="scss" scoped>
.q-label {
    font-size: 20px;
}
</style>
